import { useEffect, useMemo } from 'react'
import { Material, MeshStandardMaterial } from 'three'

import {
  useCompressedGltf,
  useDecorationSpawner,
  useInstancedGeoFamily,
  useLevelGeometry,
  type TerrainMask,
} from '@hooks'
import { coordHash, toLambert, type ChunkConfig, type DecorationConfig } from '@utils'

const VARIANTS = ['/models/greenery/flower3.glb', '/models/greenery/flower4.glb'] as const
const NODES = ['Petal_3', 'Petal_4'] as const
const S = 100

function cloneOpaqueMaterial(mat: MeshStandardMaterial, alphaTest: number) {
  const m = toLambert(mat)
  m.transparent = false
  m.alphaTest = alphaTest
  m.depthWrite = true
  return m
}

function getFlowerMaterial(mat: MeshStandardMaterial) {
  return cloneOpaqueMaterial(mat, 0.5)
}

interface FlowersProps {
  mask: TerrainMask
  full?: boolean
}

export function Flowers({ mask, full = true }: FlowersProps) {
  const { size } = useLevelGeometry()
  const frontZ = size.height / 2
  const gltfs = useCompressedGltf([...VARIANTS])

  const config = useMemo<DecorationConfig>(
    () => ({
      cellSize: 0.2,
      minTrackDist: 0.25,
      minSpacing: 0.2,
      density: 1,
      allow: (wx, wz, m) => (full || wz >= -frontZ) && m.sampleDarkGreen(wx, wz),
      score: (wx, wz) => coordHash(wx, wz, S),
    }),
    [frontZ, full],
  )

  const chunkCfg = useMemo<ChunkConfig>(
    () => ({ side: mask.side, centerOffset: mask.centerOffset, chunksX: 4, chunksZ: 4 }),
    [mask],
  )

  const instances = useDecorationSpawner(mask, config, VARIANTS.length, S, 0.1, 0.2)

  const meshes = useInstancedGeoFamily(gltfs, NODES, instances, getFlowerMaterial, false, chunkCfg)

  useEffect(() => {
    return () => {
      const seenMaterials = new Set<Material>()
      meshes.forEach(mesh => {
        mesh.geometry.dispose()
        const mat = mesh.material as Material
        if (!seenMaterials.has(mat)) {
          seenMaterials.add(mat)
          mat.dispose()
        }
      })
    }
  }, [meshes])

  return meshes.map((mesh, i) => <primitive key={i} object={mesh} />)
}
